import { ActionTree } from 'vuex';
import { CustomQuestionsState, RootState } from '../types';
import { state as authState } from '../auth/auth';
import { AxiosInstance } from 'axios';
import { handleAllErrors } from '@/utils/errorHandling';

export const actions: ActionTree<CustomQuestionsState, RootState> = {

	/**
	 * Fetch all custom questions, optionally filtered by type
	 *
	 * @param {CustomQuestionCategoryType} [type] - Optional type filter
	 * @return {Promise<void>}
	 */
	async fetchCustomQuestions({ commit }, type?: CustomQuestionCategoryType): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const params = type ? { type } : {};
			const questions: CustomQuestion[] = (await axiosInst.get<CustomQuestion[]>('/custom-questions', { params })).data;
			commit('SET_CUSTOM_QUESTIONS', questions);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Create a new custom question
	 *
	 * @param {Partial<CustomQuestion>} questionData - The question data to create
	 * @return {Promise<CustomQuestion | undefined>}
	 */
	async createCustomQuestion({ dispatch }, questionData: Partial<CustomQuestion>): Promise<CustomQuestion | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const { data } = await axiosInst.post<CustomQuestion>('/custom-questions', questionData);
			await dispatch('fetchCustomQuestions');
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Update an existing custom question
	 *
	 * @param {CustomQuestion} questionData - The question data with id
	 * @return {Promise<CustomQuestion | undefined>}
	 */
	async updateCustomQuestion({ dispatch }, questionData: CustomQuestion): Promise<CustomQuestion | undefined> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			const { data } = await axiosInst.put<CustomQuestion>(`/custom-questions/${questionData.id}`, questionData);
			await dispatch('fetchCustomQuestions');
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	/**
	 * Delete a custom question
	 *
	 * @param {number} questionId - The id of the question to delete
	 * @return {Promise<void>}
	 */
	async deleteCustomQuestion({ dispatch }, questionId: number): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;

		try {
			await axiosInst.delete<CustomQuestion>(`/custom-questions/${questionId}`);
			await dispatch('fetchCustomQuestions');
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	setCustomFields({ commit }, fields: CustomQuestion[]): void {
		commit('SET_CUSTOM_FIELDS', fields);
	},

	addCustomField({ commit }, field: CustomQuestion): void {
		commit('ADD_CUSTOM_FIELD', field);
	},

	removeCustomField({ commit }, index: number): void {
		commit('REMOVE_CUSTOM_FIELD', index);
	},

	setCustomFieldsInitialized({ commit }, initialized: boolean): void {
		commit('SET_CUSTOM_FIELDS_INITIALIZED', initialized);
	},

	resetCustomFields({ commit }): void {
		commit('RESET_CUSTOM_FIELDS');
	}
};
